<template>
  <div class="payment-page">
    <div class="container">
      <div class="row justify-content-center">
        <div class="col-lg-8 col-xl-6">
          <div class="payment-card">
            <div class="payment-header">
              <h3 class="payment-title">{{ $t('payment.title') }}</h3>
            </div>
            
            <div v-if="loading" class="text-center py-5">
              <!-- Payment Summary while loading -->
              <div v-if="reservationData" class="payment-summary mb-4">
                <h5>{{ $t('payment.summary') }}</h5>
                <div class="summary-item">
                  <span>{{ $t('reservation.totalPrice') }}:</span>
                  <strong>€{{ formatPrice(reservationData.totalPrice || reservationData.prePaymentAmount) }}</strong>
                </div>
                <div v-if="reservationData.prePaymentPercentage && reservationData.prePaymentPercentage < 100" class="summary-item highlight">
                  <span>{{ $t('reservation.payNow') }} ({{ reservationData.prePaymentPercentage }}%):</span>
                  <strong class="text-primary">€{{ formatPrice(reservationData.prePaymentAmount) }}</strong>
                </div>
                <div v-else class="summary-item">
                  <span>{{ $t('reservation.payNow') }}:</span>
                  <strong class="text-primary">€{{ formatPrice(reservationData.prePaymentAmount) }}</strong>
                </div>
              </div>
              
              <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
              <p class="mt-3 loading-text">{{ $t('payment.loading') }}</p>
              <p class="mt-2">{{ $t('payment.redirecting') }}</p>
            </div>

            <div v-else-if="error" class="alert alert-danger">
              {{ error }}
              <div class="mt-3">
                <button class="btn btn-secondary" @click="$router.push({ name: 'Experiences' })">
                  {{ $t('payment.backToExperiences') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API_HELPER } from '@/helpers/api.js';

export default {
  name: 'Payment',
  data() {
    return {
      loading: true,
      error: null,
      amount: 0,
      experienceName: '',
      reservationData: null
    };
  },
  async mounted() {
    try {
      // Check if we're coming back from Stripe (has session_id in query)
      if (this.$route.query.session_id) {
        // Redirect to success page
        this.$router.push({
          name: 'PaymentSuccess',
          query: { session_id: this.$route.query.session_id }
        });
        return;
      }

      // Load reservation data from Vuex store (more secure than sessionStorage)
      const pendingReservation = this.$store.getters['payment/pendingReservation'];
      if (!pendingReservation) {
        this.error = this.$t('payment.noReservation');
        this.loading = false;
        return;
      }

      this.reservationData = pendingReservation;
      this.amount = this.reservationData.prePaymentAmount || 0;

      // Load experience name
      await this.loadExperienceName();

      // Create checkout session and redirect to Stripe
      await this.createCheckoutSession();
    } catch (error) {
      console.error('Error initializing payment:', error);
      this.error = error.message || this.$t('payment.initError');
      this.loading = false;
    }
  },
  methods: {
    async loadExperienceName() {
      try {
        const url = API_HELPER.pathJoin([process.env.VUE_APP_API_ROOT, 'Experiences', this.reservationData.experienceId.toString()]);
        const requestOptions = {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        };
        const response = await API_HELPER.apiCall(url, null, requestOptions);
        if (response && response.data) {
          this.experienceName = response.data.name || '';
        }
      } catch (error) {
        console.error('Error loading experience:', error);
      }
    },
    formatPrice(price) {
      if (!price) return '0.00';
      return parseFloat(price).toFixed(2);
    },
    async createCheckoutSession() {
      try {
        const url = API_HELPER.pathJoin([process.env.VUE_APP_API_ROOT, 'Payments', 'create-checkout-session']);
        const requestOptions = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            experienceId: this.reservationData.experienceId,
            experienceAvailabilityId: this.reservationData.experienceAvailabilityId,
            amount: this.amount,
            numberOfPeople: this.reservationData.numberOfPeople,
            experienceName: this.experienceName,
            reservationData: {
              experienceId: this.reservationData.experienceId,
              experienceAvailabilityId: this.reservationData.experienceAvailabilityId,
              touristName: this.reservationData.touristName,
              touristEmail: this.reservationData.touristEmail,
              touristPhone: this.reservationData.touristPhone,
              reservationDate: this.reservationData.reservationDate,
              numberOfPeople: this.reservationData.numberOfPeople,
              language: this.reservationData.language,
              restrictions: this.reservationData.restrictions || '',
              notes: this.reservationData.notes || '',
              prePaymentAmount: this.amount,
              voucherId: this.reservationData.voucherId || null,
              discountAmount: this.reservationData.discountAmount || 0,
              people: this.reservationData.people || []
            }
          })
        };
        
        const response = await API_HELPER.apiCall(url, null, requestOptions);
        
        if (!response || !response.url) {
          throw new Error('Invalid response from checkout session creation');
        }

        // Redirect to Stripe Checkout page
        window.location.href = response.url;
      } catch (error) {
        console.error('Error creating checkout session:', error);
        this.error = error.message || this.$t('payment.intentError');
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.payment-page {
  padding: 3rem 0;
  min-height: 60vh;
}

.payment-card {
  background: #ffffff;
  border-radius: 24px;
  padding: 3rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04);
}

.payment-header {
  text-align: center;
  margin-bottom: 2rem;
}

.payment-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--secondary-color);
  margin: 0;
}

.payment-summary {
  background: #f9fafb;
  padding: 1.5rem;
  border-radius: 12px;
  border: 2px solid #e5e7eb;
}

.payment-summary h5 {
  margin-bottom: 1rem;
  color: var(--secondary-color);
}

.summary-item {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e5e7eb;
}

.summary-item:last-child {
  border-bottom: none;
}

.summary-item span {
  color: #6b7280;
}

.summary-item strong {
  color: var(--secondary-color);
  font-size: 1.1rem;
}

.summary-item.highlight {
  background: rgba(177, 209, 160, 0.1);
  margin: 0.5rem -1.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border-bottom: none;
}

.summary-item .text-primary {
  color: var(--primary-color) !important;
}

#payment-element {
  padding: 1.5rem;
  background: #f9fafb;
  border-radius: 12px;
  border: 2px solid #e5e7eb;
}

.btn-primary {
  background: linear-gradient(135deg, var(--primary-color) 0%, #9fc48a 100%);
  border: none;
  padding: 1rem 2rem;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(177, 209, 160, 0.4);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.loading-text {
  transform: none !important;
  writing-mode: horizontal-tb !important;
  text-orientation: mixed !important;
}

@media (max-width: 768px) {
  .payment-card {
    padding: 2rem 1.5rem;
  }

  .payment-title {
    font-size: 1.5rem;
  }
}
</style>

